// Simulated sessions: deterministic per seed, so a board and a stock page opened with the same
// ?seed show the same day. Used offline and whenever no live feed is connected.
import type { Market } from './market';
import type { DailyCandle, Session, StockSession } from './types';
import type { UniverseRow } from './markets/nse/universe';

export function mulberry32(a: number): () => number {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function gauss(rand: () => number): number {
  let u = 0, v = 0;
  while (u === 0) u = rand();
  while (v === 0) v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

// `bars` percentage increments summing to roughly `total`. Drift comes in a few regimes so
// trends start and stall the way real sessions do.
function drivenPath(rand: () => number, bars: number, total: number, vol: number): number[] {
  const regimes = 2 + Math.floor(rand() * 3);
  const cuts = Array.from({ length: regimes - 1 }, () => Math.floor(rand() * bars)).sort((a, b) => a - b);
  const weights = Array.from({ length: regimes }, () => Math.max(0, rand() * 1.6 - 0.3));
  const w: number[] = [];
  let seg = 0, acc = 0;
  for (let i = 0; i < bars; i++) {
    while (seg < cuts.length && i >= cuts[seg]) seg++;
    w.push(weights[seg]);
    acc += weights[seg];
  }
  return w.map((x) => (acc > 0 ? (total * x) / acc : total / bars) + gauss(rand) * vol);
}

/** Stable pseudo-price for stocks with no reference price. */
export function refPrice(symbol: string): number {
  let h = 2166136261;
  for (const ch of symbol) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return 40 + ((h >>> 0) % 3000);
}

export interface SimUniverse {
  /** Primary universe (e.g. NSE F&O): drawn from the main random stream. */
  primary: UniverseRow[];
  /** Extra index members: drawn from a separate stream, so adding them never shifts primary prices. */
  extra: [string, string][];
  sectors: readonly string[];
}

export function generateSession(market: Market, universe: SimUniverse, seed: number, date: string): Session {
  const BARS = market.bars;
  const rand = mulberry32(seed);
  const marketPath = drivenPath(rand, BARS, gauss(rand) * 0.35 + 0.05, 0.03);
  const sectorPaths: Record<string, number[]> = {};
  for (const s of universe.sectors) sectorPaths[s] = drivenPath(rand, BARS, gauss(rand) * 0.5, 0.025);
  const extraRand = mulberry32(seed ^ 0x5eed);
  const sectorPath = (sec: string) =>
    sectorPaths[sec] || (sectorPaths[sec] = drivenPath(extraRand, BARS, gauss(extraRand) * 0.5, 0.025));

  const rows: UniverseRow[] = [
    ...universe.primary,
    ...universe.extra.map(([symbol, sector]) => [symbol, sector, refPrice(symbol)] as UniverseRow),
  ];
  const primaryCount = universe.primary.length;

  const stocks: StockSession[] = rows.map(([symbol, sector, ref], n) => {
    const r = n < primaryCount ? rand : extraRand;
    const beta = 0.7 + r() * 0.7;
    const vol = 0.05 + r() * 0.11;
    const idio = drivenPath(r, BARS, gauss(r) * 0.9, vol);
    if (r() < 0.25) idio[Math.floor(r() * BARS)] += gauss(r) * 0.9; // news shock
    const prevClose = Math.round(ref * (0.94 + r() * 0.12) * 100) / 100;
    const avgDayRangePct = vol * Math.sqrt(BARS) * 2.2;
    const prevHigh = prevClose * (1 + ((0.15 + Math.abs(gauss(r)) * 0.5) * avgDayRangePct) / 100);
    const prevLow = prevClose * (1 - ((0.15 + Math.abs(gauss(r)) * 0.5) * avgDayRangePct) / 100);
    const avgDayVolume = Math.round(2e5 + r() * 6e6);
    const prevOI = Math.round(avgDayVolume * (6 + r() * 10));
    const oiDay = gauss(r) * 4;

    let price = prevClose * (1 + (gauss(r) * 0.25) / 100);
    const bars = [];
    for (let i = 0; i < BARS; i++) {
      const ret = beta * marketPath[i] + sectorPath(sector)[i] + idio[i];
      const o = price;
      const c = o * (1 + ret / 100);
      const wick = vol * 0.4;
      const h = Math.max(o, c) * (1 + (Math.abs(gauss(r)) * wick) / 100);
      const l = Math.min(o, c) * (1 - (Math.abs(gauss(r)) * wick) / 100);
      const v = Math.round(
        ((avgDayVolume * market.volumeCurve(i)) / market.curveTotal) * (0.6 + 0.8 * r()) * (1 + (Math.abs(ret) / vol) * 0.35)
      );
      const oi = Math.round(prevOI * (1 + ((oiDay / 100) * (i + 1)) / BARS + gauss(r) * 0.002));
      // Order flow leans with the bar's direction, with noise so it sometimes disagrees.
      const tilt = Math.max(-0.35, Math.min(0.35, (ret / vol) * 0.12 + gauss(r) * 0.08));
      const bv = Math.round(v * (0.5 + tilt));
      const book = avgDayVolume * 0.02 * (0.6 + r() * 0.8);
      bars.push({ o, h, l, c, v, oi, bv, sv: v - bv, bq: Math.round(book * (1 + tilt)), sq: Math.round(book * (1 - tilt)) });
      price = c;
    }
    return { symbol, sector, prevClose, avgDayVolume, prevOI, prevHigh, prevLow, avgDayRangePct, bars };
  });

  return { source: 'simulated', market: market.id, seed, date, stocks };
}

/** 20 simulated daily candles before the session, walking back from the previous close. */
export function simHistory(stock: StockSession, seed: number, date: string, days = 20): DailyCandle[] {
  let h = seed >>> 0;
  for (const ch of stock.symbol) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const rand = mulberry32(h);
  const out: DailyCandle[] = [];
  let close = stock.prevClose;
  const day = new Date(date + 'T00:00:00Z');
  const rangePct = stock.avgDayRangePct ?? 2;
  for (let k = 0; k < days; k++) {
    do day.setUTCDate(day.getUTCDate() - 1);
    while (day.getUTCDay() === 0 || day.getUTCDay() === 6);
    const range = (rangePct * (0.6 + rand() * 0.8)) / 100;
    const open = close / (1 + gauss(rand) * range * 0.45);
    const hi = Math.max(open, close) * (1 + rand() * range * 0.4);
    const lo = Math.min(open, close) * (1 - rand() * range * 0.4);
    out.push({ date: day.toISOString().slice(0, 10), o: open, h: hi, l: lo, c: close, v: Math.round(stock.avgDayVolume * (0.6 + rand() * 0.8)) });
    close = open * (1 + gauss(rand) * range * 0.25);
  }
  return out.reverse();
}
